import { IconName, IconPaths } from "./iconPaths";
import { ComplexIconElement } from "./types";

export interface BaseIconProps {
  className?: string;
  strokeWidth?: number;
  fillColor?: string;
}

const renderComplexElement = (element: ComplexIconElement, index: number): JSX.Element => {
  // Only include defined properties to avoid overriding SVG inheritance
  const commonProps: Record<string, unknown> = { key: index };
  if (element.id) commonProps.id = element.id;
  if (element.className) commonProps.className = element.className;
  if (element.transform) commonProps.transform = element.transform;
  if (element.fill) commonProps.fill = element.fill;
  if (element.stroke) commonProps.stroke = element.stroke;
  if (element.strokeWidth) commonProps.strokeWidth = element.strokeWidth;
  if (element.strokeDasharray) commonProps.strokeDasharray = element.strokeDasharray;
  if (element.strokeDashoffset) commonProps.strokeDashoffset = element.strokeDashoffset;
  if (element.strokeOpacity !== undefined) commonProps.strokeOpacity = element.strokeOpacity;
  if (element.fillOpacity !== undefined) commonProps.fillOpacity = element.fillOpacity;
  if (element.opacity !== undefined) commonProps.opacity = element.opacity;
  if (element.fillRule) commonProps.fillRule = element.fillRule;
  if (element.strokeLinecap) commonProps.strokeLinecap = element.strokeLinecap;
  if (element.strokeLinejoin) commonProps.strokeLinejoin = element.strokeLinejoin;
  if (element.clipRule) commonProps.clipRule = element.clipRule;

  switch (element.type) {
    case 'path':
      return <path {...commonProps} d={element.d} />;

    case 'circle':
      return (
        <circle
          {...commonProps}
          cx={element.cx}
          cy={element.cy}
          r={element.r}
        />
      );

    case 'rect':
      return (
        <rect
          {...commonProps}
          x={element.x}
          y={element.y}
          width={element.width}
          height={element.height}
          rx={element.rx}
          ry={element.ry}
        />
      );

    case 'ellipse':
      return (
        <ellipse
          {...commonProps}
          cx={element.cx}
          cy={element.cy}
          rx={element.rx}
          ry={element.ry}
        />
      );

    case 'line':
      return (
        <line
          {...commonProps}
          x1={element.x1}
          y1={element.y1}
          x2={element.x2}
          y2={element.y2}
        />
      );

    case 'polygon':
      return <polygon {...commonProps} points={element.points} />;

    case 'polyline':
      return <polyline {...commonProps} points={element.points} />;

    case 'g':
      return (
        <g {...commonProps}>
          {element.children?.map(renderComplexElement)}
        </g>
      );

    case 'symbol':
      return (
        <symbol {...commonProps}>
          {element.children?.map(renderComplexElement)}
        </symbol>
      );

    default:
      return <g key={index} />;
  }
};
export const BaseIcon: React.FC<{ name: IconName } & BaseIconProps> = ({
  name,
  className = "h-6 w-6",
  strokeWidth = 2,
  fillColor = "currentColor"
}) => {
  const config = IconPaths[name];
  if(!config){
    return <></>

  }
  const viewBox = config.viewBox ?? "0 0 24 24";

  // Icônes stroke classiques
  if (config.type === 'stroke') {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox={viewBox}
        stroke="currentColor"
        strokeWidth={strokeWidth}
        className={className}
        data-slot="icon"
      >
        {config.paths?.map((d, idx) => (
          <path key={idx} strokeLinecap="round" strokeLinejoin="round" d={d} />
        ))}
      </svg>
    );
  }

  // Icônes fill classiques
  if (config.type === 'fill') {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill={fillColor}
        viewBox={viewBox}
        className={className}
        data-slot="icon"
      >
        {config.paths?.map((d, idx) => (
          <path key={idx} d={d} />
        ))}
      </svg>
    );
  }

  // Icônes complexes (avec éléments multiples, groupes, etc.)
  if (config.type === 'complex') {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill={fillColor}
        viewBox={viewBox}
        className={className}
        data-slot="icon"
      >
        {config.elements?.map(renderComplexElement)}
      </svg>
    );
  }

  return null;
};

/**
 * Factory pour créer un composant d'icône spécifique
 */
export function createIcon(name: IconName) {
  const IconComponent: React.FC<BaseIconProps> = (props) => <BaseIcon name={name} {...props} />;
  IconComponent.displayName = `${name[0].toUpperCase()}${name.slice(1)}Icon`;
  return IconComponent;
}
